import React, { useEffect, useMemo, useRef, useState } from "react";
import * as S from "./ProgressAnimationButton.styles";

type TVariant = "primary" | "secondary";

interface IProgressAnimationButtonProps {
  isActive: boolean;
  playbackTimeInSeconds: number;
  canPlayAnimation: boolean;
  text: string;
  icon?: React.ReactNode;
  shouldCancelAnimation?: boolean;
  handleClick: () => void;
  variant?: TVariant;
}

export const ProgressAnimationButton = ({
  isActive,
  playbackTimeInSeconds,
  canPlayAnimation,
  text,
  icon,
  variant = "primary",
  handleClick,
}: Readonly<IProgressAnimationButtonProps>) => {
  const ref = useRef<HTMLButtonElement>(null);
  const [dimensions, setDimensions] = useState<DOMRect | null>(null);

  //  !!! Need text dependency to re-execute effect on language change
  useEffect(() => {
    const rect = ref?.current?.getBoundingClientRect();
    setDimensions(rect);
  }, [text]);

  const dashArrayValue = useMemo(() => {
    if (!dimensions?.width || !dimensions?.height) {
      return 0;
    }

    const rx = dimensions.height / 2;

    return (
      2 * (dimensions.width + dimensions.height) - 8 * rx + 2 * Math.PI * rx
    );
  }, [dimensions]);

  return (
    <S.StyledButton
      ref={ref}
      $variant={variant}
      $isActive={isActive}
      $isAnimationActive={canPlayAnimation}
      $playbackTime={playbackTimeInSeconds}
      onClick={handleClick}
    >
      {!!dimensions?.width && (
        <svg
          id="progress-animation-svg"
          viewBox={`0 0 ${dimensions?.width + 2} ${dimensions?.height + 2}`}
          fill="none"
          preserveAspectRatio="none"
        >
          <rect
            vectorEffect="non-scaling-stroke"
            x="1"
            y="1"
            width={dimensions?.width}
            height={dimensions?.height}
            rx={dimensions?.height / 2}
            strokeDasharray={dashArrayValue}
            strokeDashoffset={dashArrayValue}
            strokeWidth={2}
          />
        </svg>
      )}

      {!!icon && <S.IconWrapper>{icon}</S.IconWrapper>}
      <S.Label dangerouslySetInnerHTML={{ __html: text }} />
    </S.StyledButton>
  );
};
